const { find, remove } = require('lodash');

class AddressService {
    constructor($timeout, $q, restangular, trolleyService) {
        this._addressRoute = restangular.one('address');
        this.$timeout = $timeout;
        this.$q = $q;
        this.trolleyService = trolleyService;

        this.initialize();
    }

    initialize() {
        this.suburbs = [];
        this.autoAddresses = [];
        this.savedAddress = {};
        this.searchError = {
            Id: '',
            Text: '',
        };

        this.savedError = {
            ErrorType: '',
            ErrorCode: '',
            ErrorMessage: '',
            ErrorResponse: [],
        };

        this.isNonServiced = false;
        this.autoAddressSearchTerm = '';
        this.autoAddressSource = ''; // Keep tracking of current template.
        this.nonServicedAddressResult = false;
        this.decideLater = {
            collect: { Description: 'a store which I\'ll set later' },
            delivery: { Description: 'an address which I\'ll set later' },
        };
    }

    // Internal functions
    _onSaveSuccess(response) {
        this.isNonServiced = response.IsNonServiced;

        if (response.Address && response.Address.AddressText.length > 0) {
            this.savedAddress = response.Address;
            this.trolleyService.getTrolley();
            this.savedAddressesPromise = null;
            return response;
        }

        if (response.Address && response.Address.AddressId === 0) {
            this.savedError.ErrorType = 'Authentication Error';
            this.savedError.ErrorCode = 'Shopper is guest';
            this.savedError.ErrorMessage = 'Addresses cannot be saved for guest users.';
            return false;
        }

        this.savedError.ErrorType = 'Service Stack Error';
        this.savedError.ErrorCode = 'Invalid response';
        this.savedError.ErrorMessage = 'The address has failed to save.';
        return false;
    }

    _onSaveFailure(response) {
        this.savedError = response;
        if (response.data.ResponseStatus) {
            this.savedError.ErrorMessage = response.data.ResponseStatus.Message;
        }
        return false;
    }

    _clearAutoAddresses() {
        this.searchError = {
            Id: '',
            Text: '',
        };
        this.autoAddresses = [];
    }

    // global functions.
    search(query) {
        return this._addressRoute.one('search').doPOST({
            Search: query,
        }).then(
            (response) => {
                if (response.Response && response.Response.length > 0 && response.Response[0].Id !== '') {
                    this.autoAddresses = response.Response;
                } else {
                    this.searchError = { Id: 'n/a', Text: `(0) Addresses found for ${query}` };
                }
                return true;
            },
            () => {
                this.searchError = { Id: 'n/a', Text: `An error has occured searching for ${query}` };
                return false;
            },
        );
    }

    setAddress(address) {
        this.savedAddress = address;
    }

    delete(address) {
        remove(this.savedAddresses, a => a === address);
        return this._addressRoute.one('delete').doPOST({ AddressId: address.AddressId })
            .finally(() => (this.savedAddressesPromise = null));
    }

    saveAddress(address) {
        return this._addressRoute.one('auto').doPOST({
            AddressId: address.Id,
        }).then(
            response => this._onSaveSuccess(response),
            response => this._onSaveFailure(response),
        );
    }

    updateAddress(savedAddressId, selectedAddress) {
        return this._addressRoute.one('updateAuto').doPOST({
            SavedAddressId: savedAddressId,
            SelectedAddressId: selectedAddress.Id,
        }).then(
            response => this._onSaveSuccess(response),
            response => this._onSaveFailure(response),
        );
    }

    saveAutoAddress(addr) {
        if (addr && addr.Id !== 'n/a') {
            this._clearAutoAddresses();
            this.autoAddressSearchTerm = '';
            this.autoAddressSource = addr.source;
            return this.saveAddress(addr).then((address) => {
                if (address) {
                    this.autoAddressSearchTerm = '';
                }
                return address;
            });
        }
        return this.$q.when(null);
    }

    saveManualAddress(address) {
        return this._addressRoute.one('manual').doPOST({
            Street1: address.street1,
            Street2: address.street2,
            SuburbId: address.suburbId,
            Postcode: address.postcode,
        }).then(
            response => this._onSaveSuccess(response),
            response => this._onSaveFailure(response),
        );
    }

    saveNewManualAddressForBilling(address) {
        return this._addressRoute.one('manualBilling').doPOST({
            Street1: address.street1,
            Street2: address.street2,
            SuburbId: address.suburbId,
            Postcode: address.postcode,
        });
    }

    saveNewAutoAddressForBilling(address) {
        this._clearAutoAddresses();
        this.autoAddressSearchTerm = '';
        this.autoAddressSource = address.source;
        return this._addressRoute.one('autoBilling').doPOST({
            AddressId: address.Id,
        });
    }

    setSavedAddressAsBilling(addressId) {
        return this._addressRoute.one('billing').doPOST({ addressId });
    }

    updateManualAddress(savedAddressId, newAddress) {
        return this._addressRoute.one('updateManual').doPOST({
            SavedAddressId: savedAddressId,
            Street1: newAddress.street1,
            Street2: newAddress.street2,
            SuburbId: newAddress.suburbId,
            Postcode: newAddress.postcode,
        }).then(
            response => this._onSaveSuccess(response),
            response => this._onSaveFailure(response),
        );
    }

    getSuburbs(query) {
        return this._addressRoute.one('suburb', query).get().then(
            (response) => {
                this.suburbs = response.Response && response.Response.length > 0
                    ? response.Response
                    : [{ Id: 'n/a', Text: `(0) Suburbs found for ${query}` }];
                return this.suburbs;
            },
            () => [{ Id: 'n/a', Text: `An error has occured searching for ${query}` }],
        );
    }

    getPrimaryAddress() {
        return find(this.savedAddresses, { IsPrimary: true });
    }

    setPickupAddress(address = null) {
        if (address) {
            return this._addressRoute.one('SetPickup').doPOST({ AddressId: address.AddressId }).then(() => {
                this.savedAddress.AddressId = address.AddressId;
                this.savedAddress.AddressText = address.Description;
            });
        }
        this.savedAddress.AddressId = 0;
        this.savedAddress.AddressText = this.decideLater.collect.Description;
        return this.$q.when(this.savedAddress);
    }

    setDeliveryAddress(address = null) {
        if (address) {
            return this._addressRoute.one('SetDelivery').doPOST({ AddressId: address.AddressId }).then((data) => {
                this.savedAddress.AddressId = address.AddressId;
                this.savedAddress.AddressText = address.AddressText;
                this.isNonServiced = data.IsServiceNotAvailable;
            });
        }
        this.savedAddress.AddressId = 0;
        this.savedAddress.AddressText = this.decideLater.delivery.Description;
        return this.$q.when(this.savedAddress);
    }

    getPickupAddresses(args) {
        return this._addressRoute.one('pickupaddresses').get(args).then(data => data.Response);
    }

    getSavedAddresses() {
        if (!this.savedAddressesPromise) {
            this.savedAddressesPromise = this._addressRoute.get().then(
                (response) => {
                    this.savedAddresses = response.Address;
                    return true;
                },
                () => {
                    this.savedAddresses = [];
                    return false;
                });
        }
        return this.savedAddressesPromise;
    }

    saveSelectedSavedAddress(address) {
        this.savedAddress.AddressId = address.AddressId;
        this.savedAddress.AddressText = address.AddressText;
        this.savedAddress.IsPrimary = address.IsPrimary;
        return this.setDeliveryAddress(address);
    }

    isNonServicedAddress(postcode, amasAddressId) {
        return this._addressRoute.one('IsNonService').get({
            Postcode: postcode,
            AmasAddressId: amasAddressId,
        });
    }

    setPartnerDeliveryAddress(address) {
        return this._addressRoute.one('SetDelivery').one('PartnerBranch', address.PartnerBranchId).post()
            .then(() => {
                this.savedAddress = address;
                this.trolleyService.getTrolley();
            });
    }

    removePartnerAddressAssociation(branchId, addressId) {
        return this._addressRoute.post('DeletePartnerBranchAssociation', {
            PartnerBranchId: branchId,
            PartnerBranchAddressId: addressId,
        });
    }

    addressOnType(searchTerm = this.autoAddressSearchTerm) {
        this._clearAutoAddresses();
        return this.search(searchTerm)
            .then(response => (response ? this.autoAddresses : []));
    }

    addressOnSelectNonService(addr) {
        this.nonServicedAddressResult = false;
        this.autoAddressSource = addr.source;
        this.isNonServicedAddress(addr.Postcode, addr.Id).then((data) => {
            if (data.IsNonServiced) {
                this.nonServicedAddressResult = true;
            }
            if (addr && addr.Id !== 'n/a') {
                this._clearAutoAddresses();
                this.autoAddressSearchTerm = addr.Text;
            }
        });
    }
}

AddressService.$inject = ['$timeout', '$q', 'Restangular', 'TrolleyService'];

module.exports = AddressService;



// WEBPACK FOOTER //
// ./src/fulfilment/address/address.service.js